import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import type { Options } from '@/scopes/common/components/Filters/Shared'
import { PopoverBody, PopoverContent } from '@/scopes/common/components/Filters/Shared'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { PricesFilter } from '@/scopes/prices/components/Filters/BaseFilter'
import { Input } from '@/scopes/prices/components/Filters/BaseFilter/Input'
import SelectedValue from '@/scopes/prices/components/Filters/BaseFilter/MultiSelectPricesFilter/SelectedValue'
import { getFiltersSelector, getPricesSelector } from '@/scopes/prices/components/selectors'
import { FiltersInPricesValues, Option } from '@/scopes/prices/domain/filters/types'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBarLegacy/DialogPrimitive'
import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { BaseInput, Button, ButtonLoaderIcon, Checkbox, CheckedChangeEvent, Icon, IconGlyph, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { equals, isEmpty, reject } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'

const Placeholder = styled(BaseInput.Content)`
  color: ${tk('color_semantic_text_placeholder')};
`

const ChipsWrapper = styled(BaseInput.Content)`
  display: flex;
`

const Content = styled(SpaceBetween)`
  padding-bottom: 0;

  @media ${tk('mq_tablet')} {
    padding: ${tk('spacing_lg')} ${tk('spacing_lg')} 0;
  }

  @media ${tk('mq_desktop_sm')} {
    width: 340px;
  }
`

interface MultiSelectPricesFilterProps {
  label: string
  placeholder: string
  confirmButton: string
  icon: IconGlyph
  analyticsEventBuilder: (selection: string | string[] | undefined, options: Option[]) => GA4AnalyticsEvent
  options: Option[]
  selectedValues: string[]
  setSelection: (selected: FiltersInPricesValues) => void
}

function MultiSelectPricesFilter({
  label,
  placeholder,
  confirmButton,
  icon,
  analyticsEventBuilder,
  options,
  selectedValues,
  setSelection,
}: MultiSelectPricesFilterProps) {
  const { isEmpty: isEmptyResponse } = usePricesSelector(getFiltersSelector)
  const [isOpen, setIsOpen] = React.useState(false) // Add isOpen state
  const [localSelectedValue, setLocalSelectedValue] = React.useState(selectedValues)
  const { loading: isFetching } = usePricesSelector(getPricesSelector)
  const { sendEvent } = useAnalytics()
  const { isTablet } = useBreakpoints()
  const disabled = isFetching || isEmptyResponse
  const doNotRender
    = (isEmpty(options) && !isEmptyResponse) || (!isTablet && !isEmptyResponse && options.length <= 1)
  React.useEffect(() => {
    setLocalSelectedValue(selectedValues)
  }, [selectedValues])

  if (doNotRender) {
    return null
  }

  const handleConfirm = () => {
    if (!isFetching) {
      setSelection(localSelectedValue)

      if (localSelectedValue.length) {
        sendEvent(analyticsEventBuilder(localSelectedValue, options))
      }
    }
  }

  function toggle() {
    if (disabled)
      return
    if (!isFetching) {
      if (!isOpen) {
        setIsOpen(true)
      }
      else {
        handleConfirm()
        setIsOpen(false)
      }
    }
  }

  const onRemoveChip = (value: string) => {
    setLocalSelectedValue(localSelectedValue.filter(v => v !== value))
    if (!isOpen) {
      setSelection(localSelectedValue.filter(v => v !== value))
    }
  }

  return (
    <>
      {isTablet && (
        <Popover.Root open={disabled ? false : isOpen} onOpenChange={toggle}>
          <Popover.Trigger>
            <MultiSelectPricesFilterInput
              label={label}
              placeholder={placeholder}
              icon={icon}
              disabled={disabled}
              options={options}
              selectedValues={selectedValues}
              localSelectedValue={localSelectedValue}
              onRemoveChip={onRemoveChip}
            />
          </Popover.Trigger>
          <Popover.Portal>
            <PopoverContent align="start">
              <PopoverBody>
                <SpaceBetween direction="y" spacing={tk('spacing_md')}>
                  <MultiSelectPricesFilterBody
                    options={options}
                    selectedValues={localSelectedValue}
                    onSelectedValueChange={setLocalSelectedValue}
                  />
                  <PricesFilter.Footer>
                    <Button $fullSize onClick={toggle} disabled={isFetching}>
                      {isFetching && <ButtonLoaderIcon />}
                      {confirmButton}
                    </Button>
                  </PricesFilter.Footer>
                </SpaceBetween>
              </PopoverBody>
            </PopoverContent>
          </Popover.Portal>
        </Popover.Root>
      )}

      {!isTablet && (
        <>
          <MultiSelectPricesFilterInput
            label={label}
            placeholder={placeholder}
            icon={icon}
            disabled={disabled}
            options={options}
            selectedValues={selectedValues}
            localSelectedValue={localSelectedValue}
            toggle={toggle}
            onRemoveChip={onRemoveChip}
          />
          <Dialog.Root open={disabled ? false : isOpen} modal={false}>
            <Dialog.Portal>
              <Dialog.Content asChild>
                <DialogPrimitive.Root>
                  <DialogPrimitive.Header>
                    <DialogPrimitive.HeaderTitle>
                      <Typography $variant="body-lg-bold">{label}</Typography>
                      <Button onClick={toggle} $hierarchy="tertiary" $withoutColor>
                        <Icon glyph="IconCrossMedium" size="md" />
                      </Button>
                    </DialogPrimitive.HeaderTitle>
                  </DialogPrimitive.Header>
                  <DialogPrimitive.Body>
                    <MultiSelectPricesFilterBody
                      options={options}
                      selectedValues={localSelectedValue}
                      onSelectedValueChange={setLocalSelectedValue}
                    />
                  </DialogPrimitive.Body>
                  <DialogPrimitive.Footer>
                    <PricesFilter.Footer>
                      <Button $fullSize onClick={toggle} disabled={isFetching}>
                        {isFetching && <ButtonLoaderIcon />}
                        {confirmButton}
                      </Button>
                    </PricesFilter.Footer>
                  </DialogPrimitive.Footer>
                </DialogPrimitive.Root>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </>
      )}
    </>
  )
}

interface MultiSelectPricesFilterInputProps {
  label: string
  placeholder: string
  icon: IconGlyph
  disabled: boolean
  options: Option[]
  selectedValues: string[]
  localSelectedValue: string[]
  toggle?: () => void
  onRemoveChip: (value: string) => void
}

function MultiSelectPricesFilterInput({
  label,
  placeholder,
  icon,
  disabled,
  options,
  selectedValues,
  localSelectedValue,
  toggle,
  onRemoveChip,
}: MultiSelectPricesFilterInputProps) {
  return (
    <Input
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      formattedSelectedValues={selectedValues.join(', ')}
      icon={icon}
      toggle={toggle}
    >
      {isEmpty(localSelectedValue)
        ? (
            <Placeholder>{placeholder}</Placeholder>
          )
        : (
            <ChipsWrapper>
              {localSelectedValue.map(value => (
                <SelectedValue
                  key={value}
                  removeOption={() => {
                    onRemoveChip(value)
                  }}
                  value={value}
                >
                  {options.find(option => option.value === value)?.name || value}
                </SelectedValue>
              ))}
            </ChipsWrapper>
          )}
    </Input>
  )
}

interface FilterProps {
  options: Options[]
  selectedValues: string[]
  onSelectedValueChange: (newSelectedValue: string[]) => void
}

function MultiSelectPricesFilterBody({ options, selectedValues, onSelectedValueChange }: FilterProps) {
  function handleCheckedChange({ checked, value }: CheckedChangeEvent) {
    if (checked) {
      onSelectedValueChange([...selectedValues, String(value)])
    }

    if (!checked) {
      onSelectedValueChange(reject(equals(value), selectedValues))
    }
  }

  return (
    <Content direction="y" spacing={tk('spacing_md')}>
      {options.map((option, index) => (
        <Checkbox
          key={index}
          label={option.name}
          name={option.name}
          value={option.value}
          onCheckedChange={handleCheckedChange}
          defaultChecked={selectedValues.includes(option.value)}
          checked={selectedValues.includes(option.value)}
          supportText={option.resultsCount?.toString()}
        />
      ))}
    </Content>
  )
}

export function mapLocalSelectionToGA4Result(localSelection: string | string[] | undefined, options: Option[]) {
  if (!localSelection)
    return ''
  return options
    .filter(option => localSelection.includes(option.value))
    .map(option => option.name)
    .join(',')
}
export default MultiSelectPricesFilter
